export type PostConfirmationEvent = {
    request: {
        userAttributes: {
            email: string;
            sub: string;
            email_verified: string;
            'cognito:user_status'?: 'EXTERNAL_PROVIDER';
            name?: string;
            given_name?: string;
            family_name?: string;
            picture?: string;
        };
    };
    triggerSource: 'PostConfirmation_ConfirmSignUp' | 'PostConfirmation_ConfirmForgotPassword';
};

export type PreSignUpTriggerSource = 'PreSignUp_SignUp' | 'PreSignUp_AdminCreateUser' | 'PreSignUp_ExternalProvider';

export type PreSignUpEvent = {
    triggerSource: PreSignUpTriggerSource;
    // On the external-provider path Cognito names the provider here, as `<ProviderName>_<subject>`
    // (e.g. `Google_10769150350006150715`). It is the only carrier guaranteed on that event: a
    // federated sign-up has no clientMetadata, because the hosted-UI redirect has no channel to this
    // trigger.
    userName?: string;
    callerContext?: {
        awsSdkVersion?: string;
        clientId?: string;
    };
    request: {
        userAttributes: {
            email: string;
            // A JSON *string*, not an array: `[{"providerName":"Google","providerType":"Google",…}]`.
            // Present once the identity is linked, which is not guaranteed at pre-sign-up time, so
            // treat it as the richer of two sources rather than the reliable one.
            identities?: string;
        };
        clientMetadata?: Record<string, string>;
        validationData?: Record<string, string>;
    };
    response: {
        autoConfirmUser: boolean;
        autoVerifyEmail: boolean;
        autoVerifyPhone: boolean;
    };
};

export type CustomEmailSenderTriggerSource =
    | 'CustomEmailSender_SignUp'
    | 'CustomEmailSender_ResendCode'
    | 'CustomEmailSender_ForgotPassword'
    | 'CustomEmailSender_UpdateUserAttribute'
    | 'CustomEmailSender_VerifyUserAttribute'
    | 'CustomEmailSender_AdminCreateUser'
    | 'CustomEmailSender_AccountTakeOverNotification';

export type CustomEmailSenderEvent = {
    userPoolId: string;
    userName: string;
    request: {
        type: 'customEmailSenderRequestV1';
        code: string;
        userAttributes: {
            email?: string;
            sub?: string;
            email_verified?: string;
        };
        clientMetadata?: Record<string, string>;
    };
    triggerSource: CustomEmailSenderTriggerSource;
};

/**
 * The event type when a lambda is invoked through API Gateway
 */
export type APIGatewayEventRequestContext = {
    resourceId: string;
    resourcePath: string;
    httpMethod: 'GET' | 'POST' | 'PUT' | 'DELETE';
    extendedRequestId: string;
    requestTime: string;
    path: string;
    accountId: string;
    protocol: string;
    stage: string;
    domainPrefix: string;
    requestTimeEpoch: number;
    requestId: string;
    identity: {
        cognitoIdentityPoolId: string;
        accountId: string;
        cognitoIdentityId: string;
        caller: string;
        sourceIp: string;
        principalOrgId: string;
        accessKey: string;
        cognitoAuthenticationType: 'authenticated';
        cognitoAuthenticationProvider: string;
        userArn: string;
        userAgent: string;
        user: string;
    };
    domainName: string;
    apiId: string;
};
